import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PlusCircle, ArrowLeft, ArrowRight, Trash2, Edit2, Edit3, Home, X, Codepen, AlertTriangle, Images } from 'lucide-react';
import { useRetrofitPhotoUpload } from '../../hooks/useRetrofitPhotoUpload';
import type { CurrentPage } from '../../App';
import PhotoCapture from '../PhotoCapture';
import PhotoGalleryModal from '../PhotoGalleryModal';
import { PhotoData } from '../../services/CameraService';

import type { RetrofitSurveyData } from '../../utils/retrofitSurveyStorage';
import { getRetrofitSurveyAddress } from '../../utils/retrofitSurveyStorage';

interface RoomSurveyContainerProps {
  onNavigate: (page: CurrentPage, associatedId?: string) => void;
  onNavigateBack: () => void;
  onNavigateNext: () => void;
  retrofitSurveyId?: string;
  surveyData?: RetrofitSurveyData;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  onGalleryStateChange?: (isOpen: boolean) => void;
}

// Define DisplayablePhotoData for photo arrays with unique IDs and dataUrls
interface DisplayablePhotoData extends PhotoData {
  displayId: string;
  dataUrl: string;      // Compressed base64 - always available for offline
  fileName?: string;    // Supabase storage path
  url?: string;         // Supabase CDN URL after upload
  uploading?: boolean;  // Upload status
  error?: string;       // Upload error message
}

// Define structure for individual radiator details
interface RadiatorDetail {
  id: string; // e.g., 'radiator-1', 'radiator-2'
  height: string;
  width: string;
  hasTRV: boolean | null;
  type?: string; // Radiator type from floor plan (with-trvs, without-trvs, electric-storage-heater, etc.)
}

// Define structure for photos specific to one radiator
interface RadiatorSpecificPhotos {
  radiatorId: string; // Corresponds to RadiatorDetail.id
  images: DisplayablePhotoData[];
}

// Define structure for individual window details
interface WindowDetail {
  id: string; // e.g., 'window-1'
  width: string;
  height: string;
  trickleVents: number;
  isDraughtProofed: boolean;
  windowType: string;
  condition: string;
  orientation: string;
  glazingGap: string;
  glazingType: string;
  photos: any[];
}

interface RoomFormData {
  roomName: string;
  roomWidth: string;
  roomLength: string;
  roomHeight: string;
  area?: number; // Room area in square meters (from floor plan polygon calculation)
  roomType: string;
  roomCondition: string;
  heatingStatus: string;
  hasRadiator: boolean | null;
  numberOfRadiators: string;
  radiatorDetails: RadiatorDetail[];
  doorUnderCutsNotes: string;
  hasVentilationFan: boolean | null;
  ventilationFanType: string;
  ledLightsCount: number;
  nonLedLightsCount: number;
  smokeAlarmsCount: number;
  carbonMonoxideAlarmsCount: number;
  heatAlarmsCount: number;
  hasSecondaryHeating: boolean | null;
  hasWindow: boolean | null;
  numberOfWindows: string;
  windowDetails: WindowDetail[];
  doorPresent: boolean | null;
  doorUndercutSize: string;
}

interface RoomPhotos {
  wallImages: DisplayablePhotoData[];
  fullHeightImage: DisplayablePhotoData[];  // Full height of internal door from floor to ceiling
  doorUnderCutsImage: DisplayablePhotoData[];  // Changed to array for consistency
  ventilationFansImage: DisplayablePhotoData[];  // Changed to array for consistency
  lightingImages: DisplayablePhotoData[];  // Changed to array for consistency
  alarmImages: DisplayablePhotoData[];  // Alarm system photos
  multipleWindowImages: DisplayablePhotoData[];
  radiatorImages: RadiatorSpecificPhotos[];
  secondaryHeatingImages: DisplayablePhotoData[];  // Secondary heating system photos
}

interface RoomData extends RoomFormData { // Removed RoomPhotos from here, will be part of the form state
  id: string;
  // Photos will be managed within RoomSurveyForm's state and combined with formData on save
}

// Mapping between abbreviations and full descriptive names
const roomNameMapping: Record<string, string> = {
  // Bedrooms
  'BR1': 'Bedroom 1', 'BR2': 'Bedroom 2', 'BR3': 'Bedroom 3', 'BR4': 'Bedroom 4', 'BR5': 'Bedroom 5',
  'BR6': 'Bedroom 6', 'BR7': 'Bedroom 7', 'BR8': 'Bedroom 8', 'BR9': 'Bedroom 9', 'BR10': 'Bedroom 10',
  // Living Rooms
  'LR1': 'Living Room 1', 'LR2': 'Living Room 2', 'LR3': 'Living Room 3', 'LR4': 'Living Room 4', 'LR5': 'Living Room 5',
  // Bathrooms
  'BTH': 'Bathroom', 'BTH2': 'Bathroom 2', 'BTH3': 'Bathroom 3', 'BTH4': 'Bathroom 4',
  // Hallways
  'H1': 'Hallway 1', 'H2': 'Hallway 2', 'H3': 'Hallway 3',
  // Landings
  'L1': 'Landing 1', 'L2': 'Landing 2', 'L3': 'Landing 3', 'L4': 'Landing 4',
  // Kitchen
  'K': 'Kitchen', 'K2': 'Kitchen 2', 'K3': 'Kitchen 3', 'K/D': 'Kitchen Diner',
  // Water Closet
  'W/C': 'WC', 'W/C2': 'WC 2', 'W/C3': 'WC 3',
  // Cupboard
  'CPD': 'Cupboard', 'CPD2': 'Cupboard 2', 'CPD3': 'Cupboard 3'
};

// Helper function to get display name in format "Full Name (Abbreviation)"
const getDisplayRoomName = (roomName: string): string => {
  if (roomNameMapping[roomName]) {
    return `${roomNameMapping[roomName]} (${roomName})`;
  }
  return roomName; // Return as-is for descriptive names or unknown abbreviations
};

// Helper function to extract abbreviation from display name or return original name
const getRoomNameValue = (displayName: string): string => {
  const match = displayName.match(/\(([^)]+)\)$/);
  if (match) {
    return match[1]; // Return the abbreviation in parentheses
  }
  return displayName; // Return as-is if no parentheses found
};

const roomNameOptions = [
  // Floor plan abbreviations (matching RoomLabelPrompt exactly)
  "BR1", "BR2", "BR3", "BR4", "BR5", "BR6", "BR7", "BR8", "BR9", "BR10",
  "LR1", "LR2", "LR3", "LR4", "LR5",
  "BTH", "BTH2", "BTH3", "BTH4",
  "H1", "H2", "H3",
  "L1", "L2", "L3", "L4",
  "K", "K2", "K3", "K/D",
  "W/C", "W/C2", "W/C3",
  "CPD", "CPD2", "CPD3",
  "Other"
];

const ventilationFanTypeOptions = [
  { value: "", label: "Select List Value(s)" },
  { value: "decentralised_mechanical", label: "Decentralised Mechanical Ventilation" },
  { value: "intermittent", label: "Intermittent Ventilation Fan" },
  { value: "centralised_mechanical", label: "Centralised Mechanical Ventilation Fans" },
  { value: "other", label: "Other Ventilation Fans" }, 
];

const numberOptions = (max: number, suffix = '' ) => {
  const options = [{ value: "", label: "Select List Value(s)" }];
  for (let i = 0; i <= max; i++) {
    options.push({ value: i.toString(), label: i.toString() });
  }
  if (suffix && max > 0) {
    options.push({ value: `${max}+`, label: `${max}+${suffix}` });
  }
  return options;
};

// New dropdown options for Windows
const glazingTypeOptions = [
  { value: "", label: "Select Glazing Type" },
  { value: "Single glazing", label: "Single glazing" },
  { value: "Double pre 2002", label: "Double pre 2002" },
  { value: "Double between 2002 and 2021", label: "Double between 2002 and 2021" },
  { value: "Double post or during 2022", label: "Double post or during 2022" },
  { value: "Double with unknown install date", label: "Double with unknown install date" },
  { value: "Secondary glazing", label: "Secondary glazing" },
  { value: "Triple pre 2002", label: "Triple pre 2002" },
  { value: "Triple between 2002 and 2021", label: "Triple between 2002 and 2021" },
  { value: "Triple post or during 2022", label: "Triple post or during 2022" },
  { value: "Triple with unknown install date", label: "Triple with unknown install date" },
  { value: "Single glazing, known data", label: "Single glazing, known data" },
  { value: "Double glazing, known data", label: "Double glazing, known data" },
  { value: "Triple glazing, known data", label: "Triple glazing, known data" },
];

const orientationOptions = [
  { value: "", label: "Select Orientation" },
  { value: "N", label: "North" },
  { value: "NE", label: "North-East" },
  { value: "E", label: "East" },
  { value: "SE", label: "South-East" },
  { value: "S", label: "South" },
  { value: "SW", label: "South-West" },
  { value: "W", label: "West" },
  { value: "NW", label: "North-West" },
];

const frameTypeOptions = [
  { value: "", label: "Select Frame Type" },
  { value: "pvc", label: "PVC" },
  { value: "wood", label: "Wood" },
  { value: "metal", label: "Metal" },
];


const initialRoomFormData: RoomFormData = {
  roomName: '',
  roomWidth: '',
  roomLength: '',
  roomHeight: '',
  roomType: '',
  roomCondition: '',
  heatingStatus: '',
  hasRadiator: null,
  numberOfRadiators: '0',
  radiatorDetails: [],
  doorUnderCutsNotes: '',
  hasVentilationFan: null,
  ventilationFanType: '',
  ledLightsCount: 0,
  nonLedLightsCount: 0,
  smokeAlarmsCount: 0,
  carbonMonoxideAlarmsCount: 0,
  heatAlarmsCount: 0,
  hasSecondaryHeating: null,
  hasWindow: null,
  numberOfWindows: '0',
  windowDetails: [],
  doorPresent: null,
  doorUndercutSize: '',
};

const initialRoomPhotos: RoomPhotos = {
  wallImages: [],
  fullHeightImage: [],  // Full height image
  doorUnderCutsImage: [],  // Now array
  ventilationFansImage: [],  // Now array
  lightingImages: [],  // Now array
  alarmImages: [],  // Alarm system photos
  multipleWindowImages: [],
  radiatorImages: [],
  secondaryHeatingImages: [],  // Now array
};

// Combined RoomData with photos for list display and saving
interface RoomListData extends RoomFormData {
    id: string;
    photos: RoomPhotos; 
}

const RoomSurveyContainer: React.FC<RoomSurveyContainerProps> = ({ onNavigate, onNavigateBack, onNavigateNext, retrofitSurveyId, surveyData, onDataUpdate, onGalleryStateChange }) => {
  console.log('[RoomSurveyContainer] Rendering. RetrofitSurveyID:', retrofitSurveyId);
  const [viewMode, setViewMode] = useState<'list' | 'form'>('list');
  const [rooms, setRooms] = useState<RoomListData[]>([]);
  const [currentRoomForEditing, setCurrentRoomForEditing] = useState<RoomListData | null>(null);

  console.log('[RoomSurveyContainer] Initial states - viewMode:', viewMode, 'currentRoomForEditing:', currentRoomForEditing, 'rooms:', rooms.length);

  // Load rooms from survey data when available (but not while actively editing)
  useEffect(() => {
    // Don't reload if we're currently editing a room - prevents overwriting unsaved changes
    if (viewMode === 'form') {
      console.log('[RoomSurveyContainer] Skipping room reload - currently editing');
      return;
    }
    
    if (surveyData?.room_details?.rooms) {
      console.log('[RoomSurveyContainer] Loading rooms from survey data:', surveyData.room_details.rooms);
      
      // Helper function to ensure photo data is always an array and clear stale flags
      const ensurePhotoArray = (data: any): DisplayablePhotoData[] => {
        if (!data) return [];
        if (Array.isArray(data)) {
          return data.map(photo => ({
            ...photo,
            uploading: false,  // Reset uploading flag
            error: undefined,   // Clear any errors
            displayId: photo.displayId || photo.id || `photo-${Date.now()}-${Math.random()}`,
            dataUrl: photo.dataUrl || photo.url || photo.localPath || ''
          }));
        }
        // Convert single photo to array
        if (data.id || data.displayId) {
          return [{
            ...data,
            uploading: false,
            error: undefined,
            displayId: data.displayId || data.id || `photo-${Date.now()}`,
            dataUrl: data.dataUrl || data.url || data.localPath || ''
          }];
        }
        return [];
      };
      
      const surveyRooms: RoomListData[] = surveyData.room_details.rooms.map(surveyRoom => {
        const radiatorDetails = (surveyRoom as any).radiatorDetails || [];
        const windowDetails = (surveyRoom as any).windowDetails || [];
        console.log(`🪟 [RoomList Mapping] Room "${surveyRoom.name}":`, {
          hasWindowDetails: !!(surveyRoom as any).windowDetails,
          windowDetailsCount: windowDetails.length,
          windowDetailsRaw: windowDetails,
          firstWindowCondition: windowDetails[0]?.condition,
          firstWindowFull: windowDetails[0]
        });

        // Properly restore all room photos using ensurePhotoArray
        const savedPhotos = (surveyRoom as any).photos || {};
        
        // Load radiator photos from savedPhotos.radiatorImages (where they're actually saved)
        let radiatorImages: RadiatorSpecificPhotos[] = [];
        if (savedPhotos.radiatorImages && Array.isArray(savedPhotos.radiatorImages)) {
          // Radiator photos exist in saved photos - use them
          radiatorImages = savedPhotos.radiatorImages.map((radEntry: any) => ({
            radiatorId: radEntry.radiatorId,
            images: ensurePhotoArray(radEntry.images)
          }));
        } else {
          // No saved radiator photos - initialize empty arrays for each radiator
          radiatorImages = radiatorDetails.map((radiator: any, index: number) => ({
            radiatorId: radiator.id || `radiator-${index + 1}`,
            images: []
          }));
        }

        const restoredPhotos: RoomPhotos = {
          wallImages: ensurePhotoArray(savedPhotos.wallImages),
          fullHeightImage: ensurePhotoArray(savedPhotos.fullHeightImage),
          doorUnderCutsImage: ensurePhotoArray(savedPhotos.doorUnderCutsImage),
          ventilationFansImage: ensurePhotoArray(savedPhotos.ventilationFansImage),
          lightingImages: ensurePhotoArray(savedPhotos.lightingImages),
          alarmImages: ensurePhotoArray(savedPhotos.alarmImages),
          multipleWindowImages: ensurePhotoArray(savedPhotos.multipleWindowImages),
          radiatorImages: radiatorImages,
          secondaryHeatingImages: ensurePhotoArray(savedPhotos.secondaryHeatingImages)
        };

        console.log(`[RoomSurveyContainer] Restored photos for room ${surveyRoom.id}:`, {
          wallImages: restoredPhotos.wallImages.length,
          doorUnderCutsImage: restoredPhotos.doorUnderCutsImage.length,
          ventilationFansImage: restoredPhotos.ventilationFansImage.length,
          lightingImages: restoredPhotos.lightingImages.length,
          multipleWindowImages: restoredPhotos.multipleWindowImages.length,
          radiatorImages: restoredPhotos.radiatorImages.length,
          radiatorImagesDetail: restoredPhotos.radiatorImages.map(r => `${r.radiatorId}: ${r.images.length} photos`)
        });

        return {
          id: surveyRoom.id,
          roomName: surveyRoom.name,
          roomWidth: surveyRoom.width?.toString() || '',
          roomLength: surveyRoom.length?.toString() || '',
          roomHeight: surveyRoom.height?.toString() || '',
          roomType: (surveyRoom as any).roomType || '',
          roomCondition: (surveyRoom as any).roomCondition || '',
          heatingStatus: (surveyRoom as any).heatingStatus || '',
          hasRadiator: (surveyRoom as any).hasRadiator || null,
          numberOfRadiators: (surveyRoom as any).numberOfRadiators || '0',
          radiatorDetails: radiatorDetails,
          doorUnderCutsNotes: surveyRoom.notes || '',
          hasVentilationFan: (() => {
            const value = (surveyRoom as any).hasVentilationFan;
            console.log(`[RoomSurvey] 📂 Loading hasVentilationFan for ${surveyRoom.id}:`, value, `(type: ${typeof value})`);
            return value ?? null;
          })(),
          ventilationFanType: (surveyRoom as any).ventilationFanType || '',
          ledLightsCount: (surveyRoom as any).ledLightsCount || 0,
          nonLedLightsCount: (surveyRoom as any).nonLedLightsCount || 0,
          smokeAlarmsCount: (surveyRoom as any).smokeAlarmsCount || 0,
          carbonMonoxideAlarmsCount: (surveyRoom as any).carbonMonoxideAlarmsCount || 0,
          heatAlarmsCount: (surveyRoom as any).heatAlarmsCount || 0,
          hasSecondaryHeating: (() => {
            const value = (surveyRoom as any).hasSecondaryHeating;
            console.log(`[RoomSurvey] 📂 Loading hasSecondaryHeating for ${surveyRoom.id}:`, value, `(type: ${typeof value})`);
            return value ?? null;
          })(),
          hasWindow: (surveyRoom as any).hasWindow || null,
          numberOfWindows: (surveyRoom as any).numberOfWindows || '0',
          windowDetails: windowDetails,
          doorPresent: (() => {
            // Support both new field name (doorPresent) and legacy names for backward compatibility
            const value = (surveyRoom as any).doorPresent ?? (surveyRoom as any).doorUndercutsPresent ?? (surveyRoom as any).doorHasUndercut;
            console.log(`[RoomSurvey] 📂 Loading doorPresent for ${surveyRoom.id}:`, value, `(type: ${typeof value})`);
            return value ?? null;
          })(),
          doorUndercutSize: (surveyRoom as any).doorUndercutSize || '',
          photos: restoredPhotos
        };
      });
      setRooms(surveyRooms);
      console.log('[RoomSurveyContainer] Loaded', surveyRooms.length, 'rooms from floor plan data');
    }
  }, [surveyData?.room_details?.rooms, viewMode]);

  const handleCreateNewRoom = () => {
    setCurrentRoomForEditing({
      id: Date.now().toString(),
      ...initialRoomFormData,
      photos: initialRoomPhotos,
    });
    setViewMode('form');
  };

  const handleEditRoom = (roomId: string) => {
    const roomToEdit = rooms.find(room => room.id === roomId);
    if (roomToEdit) {
      setCurrentRoomForEditing(roomToEdit);
      setViewMode('form');
    }
  };

  const handleDeleteRoom = (roomId: string) => {
    if (window.confirm("Are you sure you want to delete this room?")) {
      setRooms(prevRooms => prevRooms.filter(room => room.id !== roomId));
      
      // Also remove from survey data to persist the deletion
      if (onDataUpdate) {
        onDataUpdate(prev => {
          const existingSurveyRooms = prev.room_details?.rooms || [];
          const updatedSurveyRooms = existingSurveyRooms.filter(r => r.id !== roomId);
          
          return {
            ...prev,
            room_details: {
              ...prev.room_details,
              rooms: updatedSurveyRooms
            }
          };
        }, 'room_details');
      }
    }
  };

  // Auto-save function that updates room data WITHOUT closing the form
  const handleAutoSaveRoom = useCallback((roomDataToSave: RoomListData) => {
    setRooms(prevRooms => {
      const existingRoomIndex = prevRooms.findIndex(r => r.id === roomDataToSave.id);
      let updatedRooms;
      if (existingRoomIndex > -1) {
        updatedRooms = [...prevRooms];
        updatedRooms[existingRoomIndex] = roomDataToSave;
      } else {
        updatedRooms = [...prevRooms, roomDataToSave];
      }
      
      // Update survey data if available
      if (onDataUpdate) {
        onDataUpdate(prev => {
          const existingSurveyRooms = prev.room_details?.rooms || [];
          const surveyRoomIndex = existingSurveyRooms.findIndex(r => r.id === roomDataToSave.id);
          
          console.log(`[RoomSurvey] 💾 Saving hasSecondaryHeating:`, roomDataToSave.hasSecondaryHeating, `(type: ${typeof roomDataToSave.hasSecondaryHeating})`);
          console.log(`[RoomSurvey] 💾 Saving doorPresent:`, roomDataToSave.doorPresent, `(type: ${typeof roomDataToSave.doorPresent})`);
          console.log(`[RoomSurvey] 💾 Saving doorUndercutSize:`, roomDataToSave.doorUndercutSize);
          console.log(`[RoomSurvey] 💾 Saving hasVentilationFan:`, roomDataToSave.hasVentilationFan, `(type: ${typeof roomDataToSave.hasVentilationFan})`);
          
          const surveyRoomData: any = {
            id: roomDataToSave.id,
            name: getRoomNameValue(roomDataToSave.roomName),
            abbreviation: generateRoomAbbreviation(roomDataToSave.roomName),
            floor: 0, // Will be updated by floor plan integration
            usage: roomDataToSave.roomName.toLowerCase().includes('bedroom') ? 'bedroom' : 
                   roomDataToSave.roomName.toLowerCase().includes('living') ? 'living' : 
                   roomDataToSave.roomName.toLowerCase().includes('kitchen') ? 'kitchen' : 
                   roomDataToSave.roomName.toLowerCase().includes('bathroom') ? 'bathroom' : 'other',
            heatingType: roomDataToSave.hasRadiator ? 'radiator' : 'none',
            ventilationType: roomDataToSave.hasVentilationFan ? roomDataToSave.ventilationFanType : 'none',
            hasVentilationFan: roomDataToSave.hasVentilationFan,
            ventilationFanType: roomDataToSave.ventilationFanType,
            hasSecondaryHeating: roomDataToSave.hasSecondaryHeating,
            doorPresent: roomDataToSave.doorPresent,
            doorUndercutSize: roomDataToSave.doorUndercutSize,
            condition: roomDataToSave.roomCondition || 'good', // Use roomCondition field
            notes: roomDataToSave.doorUnderCutsNotes,
            photos: roomDataToSave.photos || [], // IMPORTANT: Include actual room photos (RoomPhotos structure)!
            width: parseFloat(roomDataToSave.roomWidth) || 0,
            length: parseFloat(roomDataToSave.roomLength) || 0,
            height: parseFloat(roomDataToSave.roomHeight) || 0,
            area: (parseFloat(roomDataToSave.roomWidth) || 0) * (parseFloat(roomDataToSave.roomLength) || 0),
            roomType: roomDataToSave.roomType,
            roomCondition: roomDataToSave.roomCondition,
            heatingStatus: roomDataToSave.heatingStatus,
            // CRITICAL: Include heating system and window details for autosave
            hasRadiator: roomDataToSave.hasRadiator,
            numberOfRadiators: roomDataToSave.numberOfRadiators,
            radiatorDetails: roomDataToSave.radiatorDetails || [],
            hasWindow: roomDataToSave.hasWindow,
            numberOfWindows: roomDataToSave.numberOfWindows,
            windowDetails: roomDataToSave.windowDetails || [],
            // Include lighting counts
            ledLightsCount: roomDataToSave.ledLightsCount || 0,
            nonLedLightsCount: roomDataToSave.nonLedLightsCount || 0,
            // Include alarm counts
            smokeAlarmsCount: roomDataToSave.smokeAlarmsCount || 0,
            carbonMonoxideAlarmsCount: roomDataToSave.carbonMonoxideAlarmsCount || 0,
            heatAlarmsCount: roomDataToSave.heatAlarmsCount || 0
          };
          
          let updatedSurveyRooms;
          if (surveyRoomIndex >= 0) {
            updatedSurveyRooms = [...existingSurveyRooms];
            updatedSurveyRooms[surveyRoomIndex] = { ...updatedSurveyRooms[surveyRoomIndex], ...surveyRoomData };
          } else {
            updatedSurveyRooms = [...existingSurveyRooms, surveyRoomData];
          }
          
          console.log('[RoomSurveyContainer] Auto-saving room data (keeping form open):', {
            ...surveyRoomData,
            photosCount: roomDataToSave.photos ? {
              wallImages: roomDataToSave.photos.wallImages?.length || 0,
              radiatorImages: roomDataToSave.photos.radiatorImages?.length || 0,
              radiatorImagesDetail: roomDataToSave.photos.radiatorImages?.map(r => `${r.radiatorId}: ${r.images.length} photos`) || [],
              windowImages: roomDataToSave.photos.multipleWindowImages?.length || 0,
              doorUndercut: roomDataToSave.photos.doorUnderCutsImage.length || 0,
              ventilation: roomDataToSave.photos.ventilationFansImage.length || 0,
              lighting: roomDataToSave.photos.lightingImages.length || 0
            } : 'no photos'
          });
          
          return {
            ...prev,
            room_details: {
              ...prev.room_details,
              rooms: updatedSurveyRooms
            }
          };
        }, 'room_details');
      }
      
      return updatedRooms;
    });
    // NOTE: Do NOT setViewMode('list') here - keep form open for auto-save
  }, [onDataUpdate]);

  // Manual save function that closes the form (used on Submit button)
  const handleSaveRoom = (roomDataToSave: RoomListData) => {
    handleAutoSaveRoom(roomDataToSave);
    setViewMode('list');
    setCurrentRoomForEditing(null);
  };

  // Cancel edit function that closes the form without saving
  const handleCancelEdit = () => {
    setViewMode('list');
    setCurrentRoomForEditing(null);
  };
  
  // Helper function to generate room abbreviation
  const generateRoomAbbreviation = (roomName: string): string => {
    return roomName
      .replace(/Room\s+(\d+)/i, '$1') // Remove "Room" but keep number
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 4); // Max 4 characters
  };
  
  useEffect(() => {
    console.log('[RoomSurveyContainer] Rooms state updated in useEffect:', rooms);
  }, [rooms]);

  useEffect(() => {
    console.log('[RoomSurveyContainer] viewMode changed to:', viewMode);
  }, [viewMode]);

  useEffect(() => {
    console.log('[RoomSurveyContainer] currentRoomForEditing changed:', currentRoomForEditing);
  }, [currentRoomForEditing]);

  if (viewMode === 'list') {
    console.log('[RoomSurveyContainer] Rendering LIST VIEW');
    return (
      <div className="bg-white min-h-[calc(100vh-12rem)] flex flex-col">
        {/* Modern Header Section */}
        <div className="bg-white rounded-t-2xl shadow-xl border border-slate-200/50 px-4 sm:px-6 py-4 sm:py-5 relative overflow-hidden">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 relative z-10">
            <div className="flex items-center space-x-3">
              <div className="p-2 sm:p-3 bg-slate-600 rounded-xl shadow-md">
                <Codepen className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-800">Room Details</h2>
                <p className="text-slate-600 text-xs sm:text-sm hidden sm:block">Manage your property rooms and details</p>
              </div>
            </div>
            <button
              onClick={handleCreateNewRoom}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 bg-green-600 text-white font-semibold rounded-xl shadow-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-300 hover:scale-105 text-sm sm:text-base"
            >
              <PlusCircle size={16} className="sm:w-[18px] sm:h-[18px]" /> Add New Room
            </button>
          </div>
        </div>
        
        {/* Content Section */}
        <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-4 sm:px-6 py-4 sm:py-6 flex-1">

          {rooms.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-4 sm:p-8">
              <div className="p-4 sm:p-6 bg-slate-100 rounded-full mb-4 sm:mb-6">
                <Home size={32} className="sm:w-12 sm:h-12 text-slate-400" />
              </div>
              <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-3 text-slate-700">No Rooms Added Yet</h3>
              <p className="mb-6 sm:mb-8 text-slate-600 max-w-md mx-auto text-sm sm:text-base">Start by adding your first room to begin the survey process.</p>
              <button
                onClick={handleCreateNewRoom}
                className="flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white font-semibold rounded-xl shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-300 text-sm sm:text-base"
              >
                <PlusCircle size={16} className="sm:w-[18px] sm:h-[18px]" /> Create First Room
              </button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4 flex-1 overflow-y-auto">
              {rooms.map(room => (
                <div 
                  key={room.id} 
                  className="bg-slate-50 rounded-2xl p-4 sm:p-5 border border-slate-200/50 shadow-sm hover:shadow-lg transition-all duration-300 group hover:bg-slate-100/50"
                >
                  <div className="space-y-3">
                    {/* Header row with icon, title, and action buttons */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                        <div className="p-2 sm:p-3 bg-blue-600 rounded-xl shadow-md flex-shrink-0">
                          <Home size={20} className="sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-base sm:text-lg font-bold text-slate-800 truncate">
                            {room.roomName ? getDisplayRoomName(room.roomName) : 'Unnamed Room'}
                          </h3>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 opacity-100 sm:opacity-75 sm:group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0">
                        <button 
                          onClick={() => handleEditRoom(room.id)}
                          className="p-2 sm:p-3 text-blue-600 hover:text-white hover:bg-blue-600 rounded-xl transition-all duration-200 border border-blue-200 hover:border-blue-600"
                          title="Edit Room"
                        >
                          <Edit3 size={16} className="sm:w-[18px] sm:h-[18px]" />
                        </button>
                        <button 
                          onClick={() => handleDeleteRoom(room.id)}
                          className="p-2 sm:p-3 text-red-600 hover:text-white hover:bg-red-600 rounded-xl transition-all duration-200 border border-red-200 hover:border-red-600"
                          title="Delete Room"
                        >
                          <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Unified measurement cards - consistent across all screen sizes */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                      <div className="flex items-center justify-center bg-blue-50 border border-blue-100 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full flex-shrink-0"></span>
                          <span className="text-xs sm:text-sm font-medium text-blue-700">W: {room.roomWidth || '-'}m</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-center bg-green-50 border border-green-100 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full flex-shrink-0"></span>
                          <span className="text-xs sm:text-sm font-medium text-green-700">L: {room.roomLength || '-'}m</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-center bg-orange-50 border border-orange-100 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-400 rounded-full flex-shrink-0"></span>
                          <span className="text-xs sm:text-sm font-medium text-orange-700">H: {room.roomHeight || '-'}m</span>
                        </div>
                      </div>
                      {room.roomWidth && room.roomLength ? (
                        <div className="flex items-center justify-center bg-purple-50 border border-purple-100 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full flex-shrink-0"></span>
                            <span className="text-xs sm:text-sm font-semibold text-purple-700">
                              {room.area ? room.area.toFixed(2) : (parseFloat(room.roomWidth) * parseFloat(room.roomLength)).toFixed(2)}m²
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                          <div className="flex items-center gap-1.5 sm:gap-2">
                            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-400 rounded-full flex-shrink-0"></span>
                            <span className="text-xs sm:text-sm font-medium text-gray-500">- m²</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
           <div className="mt-auto pt-4 sm:pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:gap-0 sm:justify-between">
              <button
                type="button"
                onClick={onNavigateBack}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02] text-sm sm:text-base"
              >
                <ArrowLeft size={16} className="sm:w-[18px] sm:h-[18px]" /> Previous
              </button>
              <button
                type="button"
                onClick={onNavigateNext}
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02] text-sm sm:text-base"
              >
                Next <ArrowRight size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>
            </div>
        </div>
      </div>
    );
  }

  if (!currentRoomForEditing) {
    console.error('[RoomSurveyContainer] In FORM VIEW mode but currentRoomForEditing is NULL. This should not happen. Defaulting to list view.');
    return (
        <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-lg border border-yellow-200 flex flex-col items-center justify-center min-h-[300px]">
            <AlertTriangle size={48} className="text-red-500 mb-4" />
            <p className="text-lg font-semibold text-gray-700 mb-2">Error: Room Data Unavailable</p>
            <p className="text-gray-600 mb-4">Could not load room details for editing or creation.</p>
            <button onClick={() => { setViewMode('list'); setCurrentRoomForEditing(null); }} className="mt-2 px-6 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors">
                Return to Room List
            </button>
        </div>
    );
  }

  console.log('[RoomSurveyContainer] Rendering FORM VIEW for room ID:', currentRoomForEditing.id);
  return (
    <RoomSurveyForm 
      key={currentRoomForEditing.id}
      initialRoomData={currentRoomForEditing} 
      onSave={handleSaveRoom}
      onAutoSave={handleAutoSaveRoom}
      onCancel={handleCancelEdit}
      retrofitSurveyId={retrofitSurveyId}
      surveyData={surveyData}
      onGalleryStateChange={onGalleryStateChange}
    />
  );
};

interface RoomSurveyFormProps {
  initialRoomData: RoomListData;
  onSave: (updatedRoomData: RoomListData) => void;
  onAutoSave?: (roomData: RoomListData) => void;
  onCancel: () => void;
  retrofitSurveyId?: string;
  surveyData?: RetrofitSurveyData;
  onGalleryStateChange?: (isOpen: boolean) => void;
}

const RoomSurveyForm: React.FC<RoomSurveyFormProps> = ({ initialRoomData, onSave, onAutoSave, onCancel, retrofitSurveyId, surveyData, onGalleryStateChange }) => {
  console.log('[RoomSurveyForm] Rendering. Initial room ID:', initialRoomData.id, 'RetrofitSurveyID:', retrofitSurveyId);
  // Initialize photo upload queue with explicit surveyId and property name for better file organization
  const propertyName = surveyData ? getRetrofitSurveyAddress(surveyData) : undefined;
  const { queuePhotoUpload } = useRetrofitPhotoUpload(retrofitSurveyId, propertyName);

  const [formData, setFormData] = useState<RoomFormData>(() => {
    const { photos: initialPhotosData, ...restInitialData } = initialRoomData;
    console.log(`🪟 [RoomSurvey] STEP 1 - initialRoomData received:`, {
      roomName: initialRoomData.roomName || 'NEW',
      hasWindowDetails: !!(initialRoomData as any).windowDetails,
      windowDetailsCount: ((initialRoomData as any).windowDetails || []).length,
      windowDetailsRaw: (initialRoomData as any).windowDetails,
      firstWindowCondition: ((initialRoomData as any).windowDetails || [])[0]?.condition,
      firstWindowFull: ((initialRoomData as any).windowDetails || [])[0]
    });
    // Ensure all fields from initialRoomFormData are present, especially for newly added ones
    const completeInitialFormData = { ...initialRoomFormData, ...restInitialData }; 
    console.log(`🪟 [RoomSurvey] STEP 2 - After merge with defaults:`, {
      hasWindowDetails: !!completeInitialFormData.windowDetails,
      windowDetailsCount: (completeInitialFormData.windowDetails || []).length,
      windowDetailsRaw: completeInitialFormData.windowDetails,
      firstWindowFull: (completeInitialFormData.windowDetails || [])[0]
    });
    const initializedData = { 
      ...completeInitialFormData,
      windowDetails: completeInitialFormData.windowDetails || [], 
      radiatorDetails: completeInitialFormData.radiatorDetails ? completeInitialFormData.radiatorDetails.map(rd => ({...rd, hasTRV: rd.hasTRV === undefined ? null : rd.hasTRV})) : []
    };
    console.log(`🪟 [RoomSurvey] STEP 3 - Final initialized data:`, {
      roomName: initialRoomData.roomName || 'NEW',
      windowDetailsCount: initializedData.windowDetails.length,
      windowConditions: initializedData.windowDetails.map((w: any) => ({ id: w.id, condition: w.condition }))
    });
    return initializedData;
  });
  const [photos, setPhotos] = useState<RoomPhotos>(() => {
    // Ensure all photo fields from initialRoomPhotos are present
    const initializedPhotos = { ...initialRoomPhotos, ...(initialRoomData.photos || {}) };
    console.log('📷 [RoomSurvey] Initializing photos state:', {
      roomId: initialRoomData.id,
      roomName: initialRoomData.roomName,
      hasInitialPhotos: !!(initialRoomData.photos),
      wallImagesCount: initializedPhotos.wallImages?.length || 0,
      wallImagesPreview: initializedPhotos.wallImages?.map((img: any) => ({
        id: img.displayId,
        hasDataUrl: !!img.dataUrl,
        hasUrl: !!img.url
      }))
    });
    return initializedPhotos;
  });
  const [selectedGallery, setSelectedGallery] = useState<string | null>(null);
  const [selectedRadiatorIndex, setSelectedRadiatorIndex] = useState<number | null>(null);

  const title = initialRoomData.roomName ? `Edit Room: ${initialRoomData.roomName}` : "Add New Room";

  // --- Dynamic Radiator Fields Logic ---
  useEffect(() => {
    const numRadiators = parseInt(formData.numberOfRadiators, 10);
    const currentRadiatorDetailsCount = formData.radiatorDetails.length;
    const currentRadiatorImagesCount = photos.radiatorImages.length;

    if (formData.hasRadiator && numRadiators > 0) {
      const newDetails: RadiatorDetail[] = [...formData.radiatorDetails];
      const newImages: RadiatorSpecificPhotos[] = [...photos.radiatorImages];

      if (numRadiators > currentRadiatorDetailsCount) {
        for (let i = currentRadiatorDetailsCount; i < numRadiators; i++) {
          newDetails.push({ id: `radiator-${i + 1}`, height: '', width: '', hasTRV: null });
        }
      } else if (numRadiators < currentRadiatorDetailsCount) {
        newDetails.splice(numRadiators);
      }

      if (numRadiators > currentRadiatorImagesCount) {
         for (let i = currentRadiatorImagesCount; i < numRadiators; i++) {
          newImages.push({ radiatorId: `radiator-${i + 1}`, images: [] });
        }
      } else if (numRadiators < currentRadiatorImagesCount) {
        newImages.splice(numRadiators);
      }
      
      const finalDetails = newDetails.map((detail, index) => ({ ...detail, id: `radiator-${index + 1}` }));
      const finalImages = newImages.map((imgEntry, index) => ({ ...imgEntry, radiatorId: `radiator-${index + 1}` }));

      setFormData(prev => ({ ...prev, radiatorDetails: finalDetails }));
      setPhotos(prev => ({ ...prev, radiatorImages: finalImages }));

    } else if (formData.hasRadiator === false || numRadiators === 0) {
      setFormData(prev => ({ ...prev, radiatorDetails: [] }));
      setPhotos(prev => ({ ...prev, radiatorImages: [] }));
    }
  }, [formData.hasRadiator, formData.numberOfRadiators]);

  // --- Auto-set TRV based on radiator type from floor plan ---
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      radiatorDetails: prev.radiatorDetails.map(radiator => {
        // Auto-select TRV based on type from floor plan
        if (radiator.type === 'with-trvs' && radiator.hasTRV === null) {
          return { ...radiator, hasTRV: true };
        } else if (radiator.type === 'without-trvs' && radiator.hasTRV === null) {
          return { ...radiator, hasTRV: false };
        }
        return radiator;
      })
    }));
  }, [formData.radiatorDetails.length]);

  // --- Dynamic Window Fields Logic ---
  useEffect(() => {
    const numWindows = parseInt(formData.numberOfWindows, 10);
    const currentWindowDetailsCount = formData.windowDetails.length;

    if (formData.hasWindow && numWindows > 0) {
        // Only update if the count actually changed
        if (numWindows !== currentWindowDetailsCount) {
            const newDetails: WindowDetail[] = [...formData.windowDetails];

            if (numWindows > currentWindowDetailsCount) {
                // Add new empty windows while preserving existing data
                for (let i = currentWindowDetailsCount; i < numWindows; i++) {
                    newDetails.push({ 
                        id: `window-${i + 1}`, 
                        width: '', 
                        height: '', 
                        trickleVents: 0, 
                        isDraughtProofed: false, 
                        windowType: '', 
                        condition: '', 
                        orientation: '', 
                        glazingGap: '', 
                        glazingType: '', 
                        photos: []
                    });
                }
            } else if (numWindows < currentWindowDetailsCount) {
                // Remove excess windows
                newDetails.splice(numWindows);
            }
            
            // Preserve all existing data, just update IDs
            const finalDetails = newDetails.map((detail, index) => ({ ...detail, id: `window-${index + 1}` }));
            setFormData(prev => ({ ...prev, windowDetails: finalDetails }));
        }
    } else if (formData.hasWindow === false || numWindows === 0) {
        if (formData.windowDetails.length > 0) {
            setFormData(prev => ({ ...prev, windowDetails: [] }));
        }
    }
  }, [formData.hasWindow, formData.numberOfWindows]);

  // Track photos that have been processed for migration
  const migratedPhotoIdsRef = useRef<Set<string>>(new Set());
  const migrationRunOnceRef = useRef(false);

  // Progressive Enhancement: Migrate existing base64 photos to CDN
  useEffect(() => {
    // Only run migration once on mount
    if (migrationRunOnceRef.current) return;
    migrationRunOnceRef.current = true;

    console.log('[RoomSurvey] 🔄 Checking for base64 photos to migrate to CDN');

    let totalPhotosChecked = 0;
    let hasBase64Photos = false;

    // Check regular photo fields (arrays)
    const regularPhotoFields: Array<keyof Omit<RoomPhotos, 'radiatorImages'>> = [
      'wallImages', 'doorUnderCutsImage', 'ventilationFansImage', 
      'lightingImages', 'multipleWindowImages', 'secondaryHeatingImages'
    ];

    regularPhotoFields.forEach(photoType => {
      const photoArray = photos[photoType] as DisplayablePhotoData[];
      if (!Array.isArray(photoArray)) return;

      photoArray.forEach(photo => {
        totalPhotosChecked++;

        if (migratedPhotoIdsRef.current.has(photo.displayId) || photo.uploading) {
          return;
        }

        const hasBase64Data = photo.dataUrl && photo.dataUrl.startsWith('data:image/');
        const lacksUrl = !photo.url || photo.url === '';

        if (hasBase64Data && lacksUrl) {
          hasBase64Photos = true;
          migratedPhotoIdsRef.current.add(photo.displayId);

          console.log(`[RoomSurvey] 🔄 Migrating ${photoType} photo to CDN:`, photo.displayId);

          queuePhotoUpload(
            photo.displayId,
            photo.dataUrl,
            `rooms/${initialRoomData.id}`,
            photoType,
            (url: string) => {
              setPhotos(current => ({
                ...current,
                [photoType]: (current[photoType] as DisplayablePhotoData[]).map(p =>
                  p.displayId === photo.displayId
                    ? { ...p, url, uploading: false, error: undefined, fileName: url }
                    : p
                )
              }));
              console.log(`[RoomSurvey] ✅ Migration complete: ${photo.displayId} → CDN URL`);
            },
            (error: string) => {
              console.error(`[RoomSurvey] ⚠️ Migration failed for ${photo.displayId}:`, error);
            }
          );
        }
      });
    });

    // Check radiator photos
    photos.radiatorImages.forEach((radiatorEntry, radiatorIndex) => {
      radiatorEntry.images.forEach(photo => {
        totalPhotosChecked++;

        if (migratedPhotoIdsRef.current.has(photo.displayId) || photo.uploading) {
          return;
        }

        const hasBase64Data = photo.dataUrl && photo.dataUrl.startsWith('data:image/');
        const lacksUrl = !photo.url || photo.url === '';

        if (hasBase64Data && lacksUrl) {
          hasBase64Photos = true;
          migratedPhotoIdsRef.current.add(photo.displayId);

          console.log(`[RoomSurvey] 🔄 Migrating radiator-${radiatorIndex + 1} photo to CDN:`, photo.displayId);

          queuePhotoUpload(
            photo.displayId,
            photo.dataUrl,
            `rooms/${initialRoomData.id}`,
            `radiator-${radiatorIndex + 1}`,
            (url: string) => {
              setPhotos(current => ({
                ...current,
                radiatorImages: current.radiatorImages.map(radEntry =>
                  radEntry.radiatorId === radiatorEntry.radiatorId
                    ? {
                        ...radEntry,
                        images: radEntry.images.map(img =>
                          img.displayId === photo.displayId
                            ? { ...img, url, uploading: false, error: undefined, fileName: url }
                            : img
                        )
                      }
                    : radEntry
                )
              }));
              console.log(`[RoomSurvey] ✅ Migration complete: ${photo.displayId} → CDN URL`);
            },
            (error: string) => {
              console.error(`[RoomSurvey] ⚠️ Migration failed for ${photo.displayId}:`, error);
            }
          );
        }
      });
    });

    console.log(`[RoomSurvey] 📊 Migration check complete: ${totalPhotosChecked} photos checked, ${hasBase64Photos ? 'migration queued' : 'no migration needed'}`);
  }, [photos, initialRoomData.id, queuePhotoUpload]);

  // Track initial mount to avoid saving on first render
  const isInitialMount = useRef(true);
  const lastSavedPhotosRef = useRef<string>('');
  
  // Auto-save photos immediately after capture (like PropertyRearForm)
  useEffect(() => {
    // Skip on initial mount (when photos are loaded from initialRoomData)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastSavedPhotosRef.current = JSON.stringify(photos);
      return;
    }
    
    // Only save if photos have actually changed
    const photosJson = JSON.stringify(photos);
    if (photosJson === lastSavedPhotosRef.current) {
      return; // No changes, skip save
    }
    
    // Only save if we have any photos
    const hasPhotos = 
      photos.wallImages.length > 0 ||
      photos.radiatorImages.some(r => r.images.length > 0) ||
      photos.multipleWindowImages.length > 0 ||
      photos.doorUnderCutsImage.length > 0 ||
      photos.ventilationFansImage.length > 0 ||
      photos.lightingImages.length > 0 ||
      photos.secondaryHeatingImages.length > 0;
    
    if (hasPhotos) {
      console.log('[RoomSurveyForm] 💾 Auto-saving room photos to parent state:', { roomId: initialRoomData.id, timestamp: new Date().toISOString() });
      lastSavedPhotosRef.current = photosJson; // Update saved state
      // Save entire room data including photos to parent WITHOUT closing form
      onAutoSave?.({ id: initialRoomData.id, ...formData, photos });
    }
  }, [photos]); // Only watch photos, not formData

  // Auto-save formData changes (for boolean buttons, text inputs, etc.)
  const isInitialFormDataMount = useRef(true);
  const lastSavedFormDataRef = useRef<string>('');

  useEffect(() => {
    // Skip on initial mount
    if (isInitialFormDataMount.current) {
      isInitialFormDataMount.current = false;
      lastSavedFormDataRef.current = JSON.stringify(formData);
      return;
    }

    // Only save if formData has actually changed
    const formDataJson = JSON.stringify(formData);
    if (formDataJson === lastSavedFormDataRef.current) {
      return; // No changes, skip save
    }

    console.log('[RoomSurveyForm] 💾 Auto-saving room formData to parent state:', { 
      roomId: initialRoomData.id, 
      hasSecondaryHeating: formData.hasSecondaryHeating,
      doorPresent: formData.doorPresent,
      doorUndercutSize: formData.doorUndercutSize,
      hasVentilationFan: formData.hasVentilationFan,
      timestamp: new Date().toISOString() 
    });
    lastSavedFormDataRef.current = formDataJson;
    
    // Save entire room data including formData to parent WITHOUT closing form
    onAutoSave?.({ id: initialRoomData.id, ...formData, photos });
  }, [formData]); // Watch formData changes

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBooleanButtonClick = (fieldName: keyof RoomFormData, value: boolean) => {
    console.log(`[RoomSurvey] Boolean button clicked: ${fieldName} = ${value}`);
    setFormData(prev => {
      const newState = { ...prev, [fieldName]: value };
      if (fieldName === 'hasRadiator' && value === false) {
        newState.numberOfRadiators = '0';
      }
      if (fieldName === 'hasVentilationFan' && value === false) {
        newState.ventilationFanType = '';
      }
      if (fieldName === 'hasWindow' && value === false) {
        newState.numberOfWindows = '0';
      }
       if (fieldName === 'doorPresent' && value !== true) {
        newState.doorUndercutSize = '';
      }
      console.log(`[RoomSurvey] New formData.${fieldName}:`, newState[fieldName]);
      return newState;
    });
  };

  const handleRadiatorInputChange = (radiatorIndex: number, field: 'height' | 'width' | 'type', value: string) => {
    setFormData(prev => {
      const newRadiatorDetails = [...prev.radiatorDetails];
      if (newRadiatorDetails[radiatorIndex]) {
        newRadiatorDetails[radiatorIndex] = { ...newRadiatorDetails[radiatorIndex], [field]: value };
      }
      return { ...prev, radiatorDetails: newRadiatorDetails };
    });
  };

  // New handler for radiator boolean fields (e.g., hasTRV)
  const handleRadiatorBooleanChange = (radiatorIndex: number, fieldName: 'hasTRV', value: boolean) => {
    setFormData(prev => {
        const newRadiatorDetails = [...prev.radiatorDetails];
        if (newRadiatorDetails[radiatorIndex]) {
            newRadiatorDetails[radiatorIndex] = { ...newRadiatorDetails[radiatorIndex], [fieldName]: value };
        }
        return { ...prev, radiatorDetails: newRadiatorDetails };
    });
  };

  // New handlers for window details
  const handleWindowDetailChange = (windowIndex: number, field: keyof Omit<WindowDetail, 'id' | 'isDraughtProofed' | 'photos'>, value: string | number) => {
    setFormData(prev => {
        const newWindowDetails = [...prev.windowDetails];
        if (newWindowDetails[windowIndex]) {
            newWindowDetails[windowIndex] = { ...newWindowDetails[windowIndex], [field]: value };
        }
        return { ...prev, windowDetails: newWindowDetails };
    });
  };

  const handleWindowBooleanChange = (windowIndex: number, field: 'isDraughtProofed', value: boolean) => {
    setFormData(prev => {
        const newWindowDetails = [...prev.windowDetails];
        if (newWindowDetails[windowIndex]) {
            newWindowDetails[windowIndex] = { ...newWindowDetails[windowIndex], [field]: value };
        }
        return { ...prev, windowDetails: newWindowDetails };
    });
  };

  const handleWindowTrickleVentsChange = (windowIndex: number, value: number) => {
    setFormData(prev => {
        const newWindowDetails = [...prev.windowDetails];
        if (newWindowDetails[windowIndex]) {
            newWindowDetails[windowIndex] = { ...newWindowDetails[windowIndex], trickleVents: value };
        }
        return { ...prev, windowDetails: newWindowDetails };
    });
  };
  
  const handlePhotosCaptured = (
    photoTypeOrRadiatorIndex: keyof RoomPhotos | number, 
    capturedPhotos: PhotoData[], 
    singleImage: boolean = false,
  ) => {
    if (typeof photoTypeOrRadiatorIndex === 'number') {
      const radiatorIndex = photoTypeOrRadiatorIndex;
      const newPhotos: DisplayablePhotoData[] = capturedPhotos.map((p, idx) => {
        const displayId = `radiator-${radiatorIndex + 1}-${Date.now()}-${idx}`;
        const dataUrl = `data:image/${p.format};base64,${p.base64String}`;
        return {
          ...p,
          displayId,
          dataUrl,
          uploading: true,
          fileName: undefined,
          url: undefined,
          error: undefined
        };
      });

      // Queue radiator photos for upload
      newPhotos.forEach(photo => {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          `rooms/${initialRoomData.id}`,
          `radiator-${radiatorIndex + 1}`,
          (url: string) => {
            setPhotos(current => ({
              ...current,
              radiatorImages: current.radiatorImages.map(radImgEntry => 
                radImgEntry.radiatorId === `radiator-${radiatorIndex + 1}`
                  ? {
                      ...radImgEntry,
                      images: radImgEntry.images.map(img =>
                        img.displayId === photo.displayId
                          ? { ...img, url, uploading: false, error: undefined }
                          : img
                      )
                    }
                  : radImgEntry
              )
            }));
          },
          (error: string) => {
            setPhotos(current => ({
              ...current,
              radiatorImages: current.radiatorImages.map(radImgEntry => 
                radImgEntry.radiatorId === `radiator-${radiatorIndex + 1}`
                  ? {
                      ...radImgEntry,
                      images: radImgEntry.images.map(img =>
                        img.displayId === photo.displayId
                          ? { ...img, uploading: false, error }
                          : img
                      )
                    }
                  : radImgEntry
              )
            }));
          }
        );
      });

      setPhotos(prev => {
        const newRadiatorImages = prev.radiatorImages.map(radImgEntry => {
          if (radImgEntry.radiatorId === `radiator-${radiatorIndex + 1}`) {
            return {
              ...radImgEntry,
              images: [...radImgEntry.images, ...newPhotos]
            };
          }
          return radImgEntry;
        });
        return { ...prev, radiatorImages: newRadiatorImages };
      });
    } else {
      const photoType = photoTypeOrRadiatorIndex as keyof Omit<RoomPhotos, 'radiatorImages'>;
      
      // All photo types are now arrays - support multiple images like PropertyFrontForm
      const newPhotos: DisplayablePhotoData[] = capturedPhotos.map((p, idx) => {
        const displayId = `${photoType}-${Date.now()}-${idx}`;
        const dataUrl = `data:image/${p.format};base64,${p.base64String}`;
        return {
          ...p,
          displayId,
          dataUrl,
          uploading: true,
          fileName: undefined,
          url: undefined,
          error: undefined
        };
      });

      // Queue array photos for upload
      newPhotos.forEach(photo => {
        queuePhotoUpload(
          photo.displayId,
          photo.dataUrl,
          `rooms/${initialRoomData.id}`,
          photoType,
          (url: string) => {
            setPhotos(current => ({
              ...current,
              [photoType]: Array.isArray(current[photoType])
                ? (current[photoType] as DisplayablePhotoData[]).map((p: any) =>
                    p.displayId === photo.displayId
                      ? { ...p, url, uploading: false, error: undefined }
                      : p
                  )
                : current[photoType]
            }));
          },
          (error: string) => {
            setPhotos(current => ({
              ...current,
              [photoType]: Array.isArray(current[photoType])
                ? (current[photoType] as DisplayablePhotoData[]).map((p: any) =>
                    p.displayId === photo.displayId
                      ? { ...p, uploading: false, error }
                      : p
                  )
                : current[photoType]
            }));
          }
        );
      });

      setPhotos(prev => {
        const currentPhotoData = prev[photoType];
        const existingArray = Array.isArray(currentPhotoData) ? currentPhotoData : [];
        return {
          ...prev,
          [photoType]: [...existingArray, ...newPhotos]
        };
      });
    }
  };
  
  const removePhoto = (
    photoTypeOrRadiatorIndex: keyof RoomPhotos | number, 
    displayIdToRemove?: string,
  ) => {
    if (typeof photoTypeOrRadiatorIndex === 'number') {
      const radiatorIndex = photoTypeOrRadiatorIndex;
      setPhotos(prev => {
        const newRadiatorImages = prev.radiatorImages.map(radImgEntry => {
          if (radImgEntry.radiatorId === `radiator-${radiatorIndex + 1}`) {
            return {
              ...radImgEntry,
              images: radImgEntry.images.filter(img => img.displayId !== displayIdToRemove)
            };
          }
          return radImgEntry;
        });
        return { ...prev, radiatorImages: newRadiatorImages };
      });
    } else {
      const photoType = photoTypeOrRadiatorIndex as keyof Omit<RoomPhotos, 'radiatorImages'>;
      setPhotos(prev => {
        const currentPhotoState = prev[photoType];
        if (Array.isArray(currentPhotoState)) {
          return { ...prev, [photoType]: currentPhotoState.filter(p => p.displayId !== displayIdToRemove) };
        } else {
          return { ...prev, [photoType]: null };
        }
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submitting Room Data:', { formData, photos });
    onSave({ id: initialRoomData.id, ...formData, photos });
  };

  const handleOpenGallery = (e: React.MouseEvent, photoType: string, radiatorIndex?: number) => {
    e.preventDefault();
    setSelectedGallery(photoType);
    if (radiatorIndex !== undefined) {
      setSelectedRadiatorIndex(radiatorIndex);
    }
  };

  const handleCloseGallery = () => {
    setSelectedGallery(null);
    setSelectedRadiatorIndex(null);
  };

  const renderPhotoPreview = (photoDatas: DisplayablePhotoData[], photoType: keyof RoomPhotos | string, altTextPrefix: string, radiatorIndex?: number) => {
    if (!photoDatas || photoDatas.length === 0) return null;

    const latestPhoto = photoDatas[photoDatas.length - 1];
    const imageUrl = latestPhoto.url || latestPhoto.dataUrl;
    
    if (!imageUrl) return null;

    return (
      <button
        type="button"
        onClick={(e) => handleOpenGallery(e, photoType as string, radiatorIndex)}
        className="relative w-20 h-20 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all hover:scale-105 ring-2 ring-slate-300 hover:ring-blue-400 flex-shrink-0 mt-2"
      >
        <img
          src={imageUrl}
          alt={altTextPrefix}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-1 right-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1">
          <Images size={12} />
          {photoDatas.length}
        </div>
      </button>
    );
  };

  const renderPhotoThumbnail = (photoData: DisplayablePhotoData | null, photoType: keyof RoomPhotos, altText: string) => {
    if (!photoData) return null;
    // Use CDN URL if available, otherwise use dataUrl
    const imageUrl = photoData.url || photoData.dataUrl;
    if (!imageUrl) return null;
    
    return (
      <div className="w-20 h-20 relative group flex-shrink-0 mt-2">
        <img src={imageUrl} alt={altText} className="w-full h-full object-cover rounded-lg border-2 border-yellow-300 shadow-md" />
        <button type="button" onClick={() => removePhoto(photoType, photoData.displayId)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity" title={`Remove ${altText}`}>X</button>
      </div>
    );
  };

  const renderMultiplePhotoThumbnails = (
    photoDatas: DisplayablePhotoData[], 
    photoTypeOrRadiatorIndex: keyof RoomPhotos | number, 
    altTextPrefix: string
  ) => {
    if (!photoDatas || photoDatas.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-3 mt-4">
        {photoDatas.map((photo) => {
          // Use CDN URL if available, otherwise use dataUrl
          const imageUrl = photo.url || photo.dataUrl;
          if (!imageUrl) return null;
          
          return (
            <div key={photo.displayId} className="w-20 h-20 relative group flex-shrink-0">
              <img src={imageUrl} alt={`${altTextPrefix} ${photo.displayId}`} className="w-full h-full object-cover rounded-lg border-2 border-yellow-300 shadow-md" />
              <button
                type="button"
                onClick={() => removePhoto(photoTypeOrRadiatorIndex, photo.displayId)}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                title={`Remove ${altTextPrefix} for ${photo.displayId}`}
              >X</button>
            </div>
          );
        })}
      </div>
    );
  };
  
  const commonSelectClassName = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400";
  const photoButtonClass = "w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 transition-all duration-200";
  const photoCaptureContainerClass = "p-4 border border-gray-200 rounded-lg bg-gray-50/30 space-y-2";
  const fieldWrapperClass = "space-y-2";
  const labelClass = "text-sm font-medium text-gray-700 flex items-center";
  const requiredMarker = <span className="text-red-500 mr-1">*</span>;
  const subLabelClass = "text-xs text-gray-500 ml-1";
  const numericInputClass = "w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
  const buttonGroupContainer = "flex space-x-4 mt-1";
  const booleanButtonClass = (isSelected: boolean | null | string, actualValue?: boolean | string) =>
    `flex-1 py-3 rounded-lg border-2 ${isSelected === actualValue ? 'bg-yellow-100 border-yellow-400 text-yellow-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'} transition-colors font-medium`;

  return (
    <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 mb-6 flex items-center gap-3">
        <Codepen />
        {formData.roomName ? `Editing: ${getDisplayRoomName(formData.roomName)}` : 'Add New Room'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6 flex-1 flex flex-col">
        <div className="flex-1 space-y-6">
          
          {/* Room Basic Information */}
            <div className="bg-slate-600 rounded-t-2xl px-5 py-4 shadow-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h4a1 1 0 011 1v5m-6 0h6" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Room Information</h3>
                <p className="text-slate-100 text-sm">Basic room details and dimensions</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-5 py-5 space-y-5">
            {/* Room Name */}
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/50 shadow-inner">
              <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                    <div className="p-2 bg-slate-200 rounded-lg mr-3 shadow-sm">
                  <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                {requiredMarker}Room Name
              </h5>
              <select id="roomName" name="roomName" value={formData.roomName} onChange={handleInputChange} className="w-full px-4 py-3 text-sm bg-white border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-400 transition-all duration-300 hover:border-slate-300 shadow-md" required>
                <option value="">Select a room name</option>
                {roomNameOptions.map(name => <option key={name} value={name}>{getDisplayRoomName(name)}</option>)}
              </select>
            </div>

            {/* Room Dimensions */}
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-200/50 shadow-inner">
              <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                <div className="p-2 bg-blue-200 rounded-lg mr-3 shadow-sm">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </div>
                Room Dimensions (meters)
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                      <label htmlFor="roomWidth" className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                        <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                        <span>{requiredMarker}Width (m)</span>
                      </label>
                      <input type="number" id="roomWidth" name="roomWidth" value={formData.roomWidth} onChange={handleInputChange} placeholder="e.g. 3.5" className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-blue-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-300 hover:border-blue-300 hover:bg-white shadow-md backdrop-blur-sm" required step="0.01" min="0"/>
                  </div>
                  <div className="space-y-2">
                      <label htmlFor="roomLength" className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                        <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                        </svg>
                        <span>{requiredMarker}Length (m)</span>
                      </label>
                      <input type="number" id="roomLength" name="roomLength" value={formData.roomLength} onChange={handleInputChange} placeholder="e.g. 4.2" className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-blue-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-300 hover:border-blue-300 hover:bg-white shadow-md backdrop-blur-sm" required step="0.01" min="0"/>
                  </div>
                  <div className="space-y-2">
                      <label htmlFor="roomHeight" className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                        <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                        </svg>
                        <span>{requiredMarker}Height (m)</span>
                      </label>
                      <input type="number" id="roomHeight" name="roomHeight" value={formData.roomHeight} onChange={handleInputChange} placeholder="e.g. 2.4" className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-blue-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-300 hover:border-blue-300 hover:bg-white shadow-md backdrop-blur-sm" required step="0.01" min="0"/>
                  </div>
              </div>
            </div>

            {/* Room Type */}
            <div className="bg-purple-50 rounded-xl p-5 border border-purple-200/50 shadow-inner">
              <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                <div className="p-2 bg-purple-200 rounded-lg mr-3 shadow-sm">
                  <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                {requiredMarker}Room Type
              </h5>
              <div className="grid grid-cols-3 gap-3">
                <button 
                  type="button" 
                  onClick={() => setFormData(prev => ({ ...prev, roomType: 'Wet Room', heatingStatus: '' }))}
                  className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                    formData.roomType === 'Wet Room'
                      ? 'bg-purple-600 text-white border-2 border-purple-400 shadow-lg shadow-purple-500/25' 
                      : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-purple-300 hover:shadow-md backdrop-blur-sm'
                  }`}
                >
                  Wet Room
                </button>
                <button 
                  type="button" 
                  onClick={() => setFormData(prev => ({ ...prev, roomType: 'Habitable' }))}
                  className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                    formData.roomType === 'Habitable'
                      ? 'bg-purple-600 text-white border-2 border-purple-400 shadow-lg shadow-purple-500/25' 
                      : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-purple-300 hover:shadow-md backdrop-blur-sm'
                  }`}
                >
                  Habitable
                </button>
                <button 
                  type="button" 
                  onClick={() => setFormData(prev => ({ ...prev, roomType: 'N/A', heatingStatus: '' }))}
                  className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                    formData.roomType === 'N/A'
                      ? 'bg-purple-600 text-white border-2 border-purple-400 shadow-lg shadow-purple-500/25' 
                      : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-purple-300 hover:shadow-md backdrop-blur-sm'
                  }`}
                >
                  N/A
                </button>
              </div>
            </div>

            {/* Room Condition */}
            <div className="bg-slate-50 rounded-xl p-5 border border-slate-200/50 shadow-inner">
              <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                <div className="p-2 bg-slate-200 rounded-lg mr-3 shadow-sm">
                  <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {requiredMarker}Room Condition
              </h5>
              <div className="grid grid-cols-3 gap-3">
                <button 
                  type="button" 
                  onClick={() => setFormData(prev => ({ ...prev, roomCondition: 'good' }))}
                  className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                    formData.roomCondition === 'good'
                      ? 'bg-emerald-600 text-white border-2 border-emerald-400 shadow-lg shadow-emerald-500/25' 
                      : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-emerald-300 hover:shadow-md backdrop-blur-sm'
                  }`}
                >
                  Good
                </button>
                <button 
                  type="button" 
                  onClick={() => setFormData(prev => ({ ...prev, roomCondition: 'average' }))}
                  className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                    formData.roomCondition === 'average'
                      ? 'bg-amber-600 text-white border-2 border-amber-400 shadow-lg shadow-amber-500/25' 
                      : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-amber-300 hover:shadow-md backdrop-blur-sm'
                  }`}
                >
                  Average
                </button>
                <button 
                  type="button" 
                  onClick={() => setFormData(prev => ({ ...prev, roomCondition: 'poor' }))}
                  className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                    formData.roomCondition === 'poor'
                      ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/25' 
                      : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-red-300 hover:shadow-md backdrop-blur-sm'
                  }`}
                >
                  Poor
                </button>
              </div>
            </div>

            {/* Heating Status - Only show if room type is Habitable */}
            {formData.roomType === 'Habitable' && (
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-amber-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                    </svg>
                  </div>
                  {requiredMarker}Heating Status
                </h5>
                <div className="flex space-x-3">
                  <button 
                    type="button" 
                    onClick={() => setFormData(prev => ({ ...prev, heatingStatus: 'Heated' }))}
                    className={`flex-1 px-4 py-3 text-sm font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                      formData.heatingStatus === 'Heated'
                        ? 'bg-amber-600 text-white border-2 border-amber-400 shadow-lg shadow-amber-500/25' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-amber-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    Heated
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setFormData(prev => ({ ...prev, heatingStatus: 'Unheated' }))}
                    className={`flex-1 px-4 py-3 text-sm font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                      formData.heatingStatus === 'Unheated'
                        ? 'bg-slate-600 text-white border-2 border-slate-400 shadow-lg shadow-slate-500/25' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    Unheated
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Wall Images */}
          <div className={photoCaptureContainerClass}>
            <label className={labelClass}>{requiredMarker}Wall Images <span className={subLabelClass}>(Capture all 4 walls)</span></label>
            <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('wallImages', p)} maxPhotos={999} buttonLabel="Capture Wall Images" buttonClass={photoButtonClass} />
            {renderPhotoPreview(photos.wallImages, 'wallImages', 'Wall Image')}
          </div>

          {/* Full Height Image */}
          <div className={photoCaptureContainerClass}>
            <label className={labelClass}>{requiredMarker}Full height of internal door from floor to ceiling image</label>
            <PhotoCapture onPhotosCaptured={(p) => handlePhotosCaptured('fullHeightImage', p)} maxPhotos={999} buttonLabel="Capture Full Height Images" buttonClass={photoButtonClass} />
            {renderPhotoPreview(photos.fullHeightImage, 'fullHeightImage', 'Full Height Image')}
          </div>

          {/* Heating System */}
          <div className="mt-6">
            <div className="bg-orange-600 rounded-t-2xl px-5 py-4 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Heating System</h3>
                  <p className="text-orange-100 text-sm">Configure heating system specifications</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-5 py-5 space-y-5">
              {/* Has Radiator Question */}
              <div className="bg-orange-50 rounded-xl p-5 border border-orange-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-orange-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  {requiredMarker}Are there radiators in this room?
                </h5>
                <div className="flex space-x-3">
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('hasRadiator', true)} 
                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                      formData.hasRadiator === true 
                        ? 'bg-green-600 text-white border-2 border-green-400 shadow-lg shadow-green-500/25 scale-105' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    Yes
                  </button>
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('hasRadiator', false)} 
                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                      formData.hasRadiator === false 
                        ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/25 scale-105' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {formData.hasRadiator && (
                <div className="bg-red-50 rounded-xl p-5 border border-red-200/50 shadow-inner">
                  <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                    <div className="p-2 bg-red-200 rounded-lg mr-3 shadow-sm">
                      <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    </div>
                    {requiredMarker}Number of Heating Systems
                  </h5>
                  <select id="numberOfRadiators" name="numberOfRadiators" value={formData.numberOfRadiators} onChange={handleInputChange} className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-red-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 transition-all duration-300 hover:border-red-300 hover:bg-white shadow-md backdrop-blur-sm" required>
                    {numberOptions(10).map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>
              )}

              {formData.hasRadiator && parseInt(formData.numberOfRadiators, 10) > 0 && formData.radiatorDetails.map((radiator, index) => (
                <div key={radiator.id} className="bg-gradient-to-br from-white via-orange-50/30 to-red-50/30 rounded-2xl p-6 border border-orange-200/40 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm">
                  <div className="flex items-center justify-between pb-5 border-b border-orange-200/50">
                    <div className="flex items-center space-x-3">
                      <div className="p-2.5 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl shadow-md">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-bold bg-gradient-to-r from-slate-800 to-slate-700 bg-clip-text text-transparent">Heating System {index + 1} Details</h4>
                        <p className="text-sm text-slate-600">Configure dimensions and TRV presence</p>
                      </div>
                    </div>
                    {radiator.type && (
                      <div className="px-3 py-1.5 bg-gradient-to-r from-orange-100 to-red-100 border border-orange-300 rounded-lg">
                        <p className="text-xs font-semibold text-orange-800">
                          {radiator.type === 'radiator' ? (radiator.hasTRV !== null && radiator.hasTRV !== undefined ? `Radiator${radiator.hasTRV ? ' (TRV)' : ' (No TRV)'}` : 'Radiator') :
                           radiator.type === 'panel-heater' ? 'Panel Heater' :
                           radiator.type === 'fan-heater' ? 'Fan Heater' :
                           radiator.type === 'electric-storage-heater' ? 'Electric Storage Heater' :
                           radiator.type === 'high-heat-retention' ? 'High Heat Retention' : radiator.type}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Type Selection */}
                  <div className="bg-purple-50 rounded-xl p-5 border border-purple-200/50 shadow-inner mt-5">
                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                      <div className="p-2 bg-purple-200 rounded-lg mr-3 shadow-sm">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                      </div>
                      {requiredMarker}Heating System Type
                    </h5>
                    <select 
                      id={`radiator-${index}-type`}
                      name={`radiator-${index}-type`}
                      value={radiator.type || 'radiator'}
                      onChange={(e) => handleRadiatorInputChange(index, 'type', e.target.value)}
                      className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-purple-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all duration-300 hover:border-purple-300 hover:bg-white shadow-md backdrop-blur-sm"
                      required
                    >
                      <option value="radiator">Radiator</option>
                      <option value="panel-heater">Panel Heater</option>
                      <option value="fan-heater">Fan Heater</option>
                      <option value="electric-storage-heater">Electric Storage Heater</option>
                      <option value="high-heat-retention">High Heat Retention</option>
                    </select>
                  </div>
                  
                  {/* Dimensions */}
                  <div className="bg-orange-50 rounded-xl p-5 border border-orange-200/50 shadow-inner mt-5">
                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                      <div className="p-2 bg-orange-200 rounded-lg mr-3 shadow-sm">
                        <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                        </svg>
                      </div>
                      Dimensions (millimeters)
                    </h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label htmlFor={`radiator-${index}-height`} className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                          <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                          </svg>
                          <span>{requiredMarker}Height (mm)</span>
                        </label>
                        <input 
                          type="number" 
                          id={`radiator-${index}-height`} 
                          name={`radiator-${index}-height`} 
                          value={radiator.height} 
                          onChange={(e) => handleRadiatorInputChange(index, 'height', e.target.value)} 
                          placeholder="e.g. 600" 
                          className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-orange-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition-all duration-300 hover:border-orange-300 hover:bg-white shadow-md backdrop-blur-sm"
                          required 
                          step="1" min="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor={`radiator-${index}-width`} className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                          <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                          <span>{requiredMarker}Width (mm)</span>
                        </label>
                        <input 
                          type="number" 
                          id={`radiator-${index}-width`} 
                          name={`radiator-${index}-width`} 
                          value={radiator.width} 
                          onChange={(e) => handleRadiatorInputChange(index, 'width', e.target.value)} 
                          placeholder="e.g. 1100" 
                          className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-orange-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/30 focus:border-orange-400 transition-all duration-300 hover:border-orange-300 hover:bg-white shadow-md backdrop-blur-sm"
                          required 
                          step="1" min="0"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* TRV Question - Only show for radiator type */}
                  {(!radiator.type || radiator.type === 'radiator') && (
                    <div className="bg-red-50 rounded-xl p-5 border border-red-200/50 shadow-inner mt-4">
                      <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                        <div className="p-2 bg-red-200 rounded-lg mr-3 shadow-sm">
                          <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          </svg>
                        </div>
                        {requiredMarker}Is TRV present in Heating System {index + 1}?
                      </h5>
                      <div className="flex space-x-3">
                        <button 
                          type="button" 
                          onClick={() => handleRadiatorBooleanChange(index, 'hasTRV', true)} 
                          className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                            radiator.hasTRV === true 
                              ? 'bg-green-600 text-white border-2 border-green-400 shadow-lg shadow-green-500/25 scale-105' 
                              : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                          }`}
                        >
                          Yes
                        </button>
                        <button 
                          type="button" 
                          onClick={() => handleRadiatorBooleanChange(index, 'hasTRV', false)} 
                          className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                            radiator.hasTRV === false 
                              ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/25 scale-105' 
                              : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                          }`}
                        >
                          No
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Photo Section */}
                  <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200/50 shadow-inner mt-4">
                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                      <div className="p-2 bg-indigo-200 rounded-lg mr-3 shadow-sm">
                        <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      {requiredMarker}Heating System {index + 1} Images
                    </h5>
                    <p className="text-xs text-slate-600 mb-4">Include clear full radiator with TRV if available</p>
                    <PhotoCapture 
                      onPhotosCaptured={(p) => handlePhotosCaptured(index, p)}
                      maxPhotos={999}
                      buttonLabel={`Capture Images for Heating System ${index + 1}`}
                      buttonClass="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-lg transition-all duration-300" 
                    />
                    {renderPhotoPreview(
                      photos.radiatorImages.find(r => r.radiatorId === radiator.id)?.images || [], 
                      `radiator_${index}`,
                      `Heating System ${index + 1}`,
                      index
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Secondary Heating System */}
          <div className="mt-6">
            <div className="bg-amber-600 rounded-t-2xl px-5 py-4 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Secondary Heating</h3>
                  <p className="text-amber-100 text-sm">Additional heating systems</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-5 py-5">
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-amber-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  {requiredMarker}Secondary Heating System Present?
                </h5>
                <div className="flex space-x-3">
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('hasSecondaryHeating', true)} 
                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                      formData.hasSecondaryHeating === true 
                        ? 'bg-green-600 text-white border-2 border-green-400 shadow-lg shadow-green-500/25 scale-105' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    Yes
                  </button>
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('hasSecondaryHeating', false)} 
                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                      formData.hasSecondaryHeating === false 
                        ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/25 scale-105' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {/* Secondary Heating Photos - Show only if secondary heating is present */}
              {formData.hasSecondaryHeating === true && (
                <div className="bg-amber-50 rounded-xl p-5 border border-amber-200/50 shadow-inner mt-5">
                  <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                    <div className="p-2 bg-amber-200 rounded-lg mr-3 shadow-sm">
                      <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    Secondary Heating System Images
                  </h5>
                  <p className="text-xs text-slate-600 mb-4">Capture images of the secondary heating system</p>
                  <PhotoCapture 
                    onPhotosCaptured={(p) => handlePhotosCaptured('secondaryHeatingImages', p)} 
                    maxPhotos={999} 
                    buttonLabel="Capture Secondary Heating Images" 
                    buttonClass="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-amber-600 rounded-xl hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 shadow-lg transition-all duration-300" 
                  />
                  {renderPhotoPreview(photos.secondaryHeatingImages, 'secondaryHeatingImages', 'Secondary Heating')}
                </div>
              )}
            </div>
          </div>

          {/* Door Present */}
          <div className="mt-6">
            <div className="bg-teal-600 rounded-t-2xl px-5 py-4 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Door Information</h3>
                  <p className="text-cyan-100 text-sm">Door presence and undercut details</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-5 py-5 space-y-5">
              {/* Door Present Question */}
              <div className="bg-teal-50 rounded-xl p-5 border border-teal-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-teal-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  {requiredMarker}Door Present?
                </h5>
                <div className="grid grid-cols-3 gap-3">
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('doorPresent', true)} 
                    className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                      formData.doorPresent === true 
                        ? 'bg-green-600 text-white border-2 border-green-400 shadow-lg shadow-green-500/25' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    Yes
                  </button>
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('doorPresent', false)} 
                    className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                      formData.doorPresent === false 
                        ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/25' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    No
                  </button>
                  <button 
                    type="button" 
                    onClick={() => setFormData(prev => ({ ...prev, doorPresent: null, doorUndercutSize: '' }))} 
                    className={`px-3 py-3 text-xs font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                      formData.doorPresent === null 
                        ? 'bg-slate-600 text-white border-2 border-slate-400 shadow-lg shadow-slate-500/25' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    N/A
                  </button>
                </div>
              </div>

              {/* Door Undercut Size - Show only if door is present */}
              {formData.doorPresent === true && (
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200/50 shadow-inner mt-5">
                  <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                    <div className="p-2 bg-blue-200 rounded-lg mr-3 shadow-sm">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                      </svg>
                    </div>
                    {requiredMarker}Door Undercut Size
                  </h5>
                  <div className="flex space-x-3">
                    <button 
                      type="button" 
                      onClick={() => setFormData(prev => ({ ...prev, doorUndercutSize: 'More Than 10mm' }))} 
                      className={`flex-1 px-4 py-3 text-sm font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                        formData.doorUndercutSize === 'More Than 10mm' 
                          ? 'bg-blue-600 text-white border-2 border-blue-400 shadow-lg shadow-blue-500/25' 
                          : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-blue-300 hover:shadow-md backdrop-blur-sm'
                      }`}
                    >
                      More Than 10mm
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setFormData(prev => ({ ...prev, doorUndercutSize: 'Less than 10mm' }))} 
                      className={`flex-1 px-4 py-3 text-sm font-semibold text-center rounded-xl transition-all duration-300 shadow-sm ${
                        formData.doorUndercutSize === 'Less than 10mm' 
                          ? 'bg-blue-600 text-white border-2 border-blue-400 shadow-lg shadow-blue-500/25' 
                          : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-blue-300 hover:shadow-md backdrop-blur-sm'
                      }`}
                    >
                      Less than 10mm
                    </button>
                  </div>
                </div>
              )}

              {/* Door Images - Show unless N/A */}
              {formData.doorPresent !== null && (
                <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-200/50 shadow-inner mt-5">
                  <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                    <div className="p-2 bg-cyan-200 rounded-lg mr-3 shadow-sm">
                      <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    {requiredMarker}Door Images
                  </h5>
                  <p className="text-xs text-slate-600 mb-4">
                    {formData.doorPresent === true 
                      ? 'Capture images showing door and undercut' 
                      : 'Capture images showing the absence of a door'}
                  </p>
                  <PhotoCapture 
                    onPhotosCaptured={(p) => handlePhotosCaptured('doorUnderCutsImage', p)} 
                    maxPhotos={999} 
                    buttonLabel={formData.doorPresent === true ? "Capture Door Images" : "Capture No Door Evidence"} 
                    buttonClass="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-cyan-600 rounded-xl hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 shadow-lg transition-all duration-300" 
                  />
                  {renderPhotoPreview(photos.doorUnderCutsImage, 'doorUnderCutsImage', 'Door')}
                </div>
              )}

            </div>
          </div>
          
          {/* Ventilation */}
          <div className="mt-6">
            <div className="bg-purple-600 rounded-t-2xl shadow-xl border border-slate-200/50 px-5 py-4 relative overflow-hidden">
              <div className="flex items-center space-x-4 relative z-10">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Ventilation</h3>
                  <p className="text-purple-100 text-sm">Air circulation and exhaust systems</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-5 py-5 space-y-5">
              {/* Ventilation Fan Question */}
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-purple-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  {requiredMarker}Ventilation Fan?
                </h5>
                <div className="flex space-x-3">
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('hasVentilationFan', true)} 
                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                      formData.hasVentilationFan === true 
                        ? 'bg-green-600 text-white border-2 border-green-400 shadow-lg shadow-green-500/25 scale-105' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    Yes
                  </button>
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('hasVentilationFan', false)} 
                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                      formData.hasVentilationFan === false 
                        ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/25 scale-105' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {/* Show fan type and photos only when "Yes" selected */}
              {formData.hasVentilationFan === true && (
                <>
                  {/* Fan Type */}
                  <div className="bg-green-50 rounded-xl p-5 border border-green-200/50 shadow-inner">
                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                      <div className="p-2 bg-green-200 rounded-lg mr-3 shadow-sm">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      {requiredMarker}Ventilation Fan Type
                    </h5>
                    <select 
                      id="ventilationFanType" 
                      name="ventilationFanType" 
                      value={formData.ventilationFanType} 
                      onChange={handleInputChange} 
                      className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-green-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all duration-300 hover:border-green-300 hover:bg-white shadow-md backdrop-blur-sm" 
                      required={formData.hasVentilationFan}
                    >
                      {ventilationFanTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                  
                  {/* Fan Photo */}
                  <div className="bg-teal-50 rounded-xl p-5 border border-teal-200/50 shadow-inner">
                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                      <div className="p-2 bg-teal-200 rounded-lg mr-3 shadow-sm">
                        <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      {requiredMarker}Ventilation Fan Image
                    </h5>
                    <p className="text-xs text-slate-600 mb-4">Multiple images of ventilation fans</p>
                    <PhotoCapture 
                      onPhotosCaptured={(p) => handlePhotosCaptured('ventilationFansImage', p)} 
                      maxPhotos={999} 
                      buttonLabel="Capture Fan Images" 
                      buttonClass="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-purple-600 rounded-xl hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 shadow-lg transition-all duration-300" 
                    />
                    {renderPhotoPreview(photos.ventilationFansImage, 'ventilationFansImage', 'Ventilation Fan')}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Lighting */}
          <div className="mt-6">
            <div className="bg-pink-600 rounded-t-2xl shadow-xl border border-slate-200/50 px-5 py-4 relative overflow-hidden">
              <div className="flex items-center space-x-4 relative z-10">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Lighting</h3>
                  <p className="text-pink-100 text-sm">Room illumination and fixtures</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-5 py-5 space-y-5">
              {/* Lighting Photo */}
              <div className="bg-pink-50 rounded-xl p-5 border border-pink-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-pink-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  {requiredMarker}Lighting Images
                </h5>
                <p className="text-xs text-slate-600 mb-4">Multiple images of lighting fixtures</p>
                <PhotoCapture 
                  onPhotosCaptured={(p) => handlePhotosCaptured('lightingImages', p)} 
                  maxPhotos={999} 
                  buttonLabel="Capture Lighting Images" 
                  buttonClass="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-pink-600 rounded-xl hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 shadow-lg transition-all duration-300" 
                />
                {renderPhotoPreview(photos.lightingImages, 'lightingImages', 'Lighting')}
              </div>
              
              {/* Light Counts */}
              <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-yellow-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  Light Counts (Updated from Floor Plan)
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* LED Lights Counter */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                      <span>💡 LED Lights</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, ledLightsCount: Math.max(0, prev.ledLightsCount - 1) }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        −
                      </button>
                      <div className="flex-1 px-4 py-3 text-center text-lg font-bold bg-white border-2 border-yellow-200 rounded-xl shadow-md">
                        {formData.ledLightsCount}
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, ledLightsCount: prev.ledLightsCount + 1 }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  
                  {/* Non-LED Lights Counter */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                      <span>🔆 Non-LED Lights</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, nonLedLightsCount: Math.max(0, prev.nonLedLightsCount - 1) }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        −
                      </button>
                      <div className="flex-1 px-4 py-3 text-center text-lg font-bold bg-white border-2 border-yellow-200 rounded-xl shadow-md">
                        {formData.nonLedLightsCount}
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, nonLedLightsCount: prev.nonLedLightsCount + 1 }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Alarms Section */}
          <div className="mt-6">
            <div className="bg-orange-600 rounded-t-2xl shadow-xl border border-slate-200/50 px-5 py-4 relative overflow-hidden">
              <div className="flex items-center space-x-4 relative z-10">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Alarms</h3>
                  <p className="text-orange-100 text-sm">Fire and safety alarm counts</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-5 py-5 space-y-5">
              {/* Alarm Counts */}
              <div className="bg-orange-50 rounded-xl p-5 border border-orange-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-orange-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </div>
                  Alarm Counts
                </h5>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Smoke Alarms Counter */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                      <span>🔥 Smoke Alarms</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, smokeAlarmsCount: Math.max(0, prev.smokeAlarmsCount - 1) }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        −
                      </button>
                      <div className="flex-1 px-4 py-3 text-center text-lg font-bold bg-white border-2 border-orange-200 rounded-xl shadow-md">
                        {formData.smokeAlarmsCount}
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, smokeAlarmsCount: prev.smokeAlarmsCount + 1 }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  
                  {/* Carbon Monoxide Alarms Counter */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                      <span>☠️ CO Alarms</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, carbonMonoxideAlarmsCount: Math.max(0, prev.carbonMonoxideAlarmsCount - 1) }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        −
                      </button>
                      <div className="flex-1 px-4 py-3 text-center text-lg font-bold bg-white border-2 border-orange-200 rounded-xl shadow-md">
                        {formData.carbonMonoxideAlarmsCount}
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, carbonMonoxideAlarmsCount: prev.carbonMonoxideAlarmsCount + 1 }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        +
                      </button>
                    </div>
                  </div>
                  
                  {/* Heat Alarms Counter */}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-700 flex items-center space-x-1.5">
                      <span>🌡️ Heat Alarms</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, heatAlarmsCount: Math.max(0, prev.heatAlarmsCount - 1) }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        −
                      </button>
                      <div className="flex-1 px-4 py-3 text-center text-lg font-bold bg-white border-2 border-orange-200 rounded-xl shadow-md">
                        {formData.heatAlarmsCount}
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, heatAlarmsCount: prev.heatAlarmsCount + 1 }))}
                        className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-lg font-bold text-lg shadow-md transition-all duration-200 active:scale-95"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alarm Images */}
              <div className="bg-orange-50 rounded-xl p-5 border border-orange-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-orange-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  Alarm Images
                </h5>
                <PhotoCapture 
                  onPhotosCaptured={(p) => handlePhotosCaptured('alarmImages', p)} 
                  maxPhotos={999} 
                  buttonLabel="Capture Alarm Images" 
                  buttonClass="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-orange-600 rounded-xl hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 shadow-lg transition-all duration-300" 
                />
                {renderPhotoPreview(photos.alarmImages, 'alarmImages', 'Alarm')}
              </div>
            </div>
          </div>

          {/* Windows */}
          <div className="mt-6">
            <div className="bg-blue-600 rounded-t-2xl shadow-xl border border-slate-200/50 px-5 py-4 relative overflow-hidden">
              <div className="flex items-center space-x-4 relative z-10">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-2m-8 8v2a2 2 0 002 2h2" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Windows</h3>
                  <p className="text-blue-100 text-sm">Window details and glazing information</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-b-2xl shadow-xl border-x border-b border-slate-200/50 px-5 py-5 space-y-5">
              {/* Windows Question */}
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200/50 shadow-inner">
                <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                  <div className="p-2 bg-blue-200 rounded-lg mr-3 shadow-sm">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  {requiredMarker}Are there windows in this room?
                </h5>
                <div className="flex space-x-3">
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('hasWindow', true)} 
                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                      formData.hasWindow === true 
                        ? 'bg-green-600 text-white border-2 border-green-400 shadow-lg shadow-green-500/25 scale-105' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    Yes
                  </button>
                  <button 
                    type="button" 
                    onClick={() => handleBooleanButtonClick('hasWindow', false)} 
                    className={`flex-1 px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm ${
                      formData.hasWindow === false 
                        ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/25 scale-105' 
                        : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {formData.hasWindow && (
                <>
                  {/* Number of Windows */}
                  <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200/50 shadow-inner">
                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                      <div className="p-2 bg-indigo-200 rounded-lg mr-3 shadow-sm">
                        <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                        </svg>
                      </div>
                      {requiredMarker}Number of Windows
                    </h5>
                    <select 
                      id="numberOfWindows" 
                      name="numberOfWindows" 
                      value={formData.numberOfWindows} 
                      onChange={handleInputChange} 
                      className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-indigo-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all duration-300 hover:border-indigo-300 hover:bg-white shadow-md backdrop-blur-sm" 
                      required={formData.hasWindow === true}
                    >
                      {numberOptions(10, '+').map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>

                  {formData.windowDetails.map((window, index) => (
                    <div key={window.id} className="bg-slate-50 rounded-2xl p-6 border border-slate-200/50 shadow-lg hover:shadow-xl transition-all duration-300">
                      <div className="flex items-center space-x-4 pb-5 border-b border-slate-200/50">
                        <div className="p-2.5 bg-blue-600 rounded-xl shadow-md">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-2m-8 8v2a2 2 0 002 2h2" />
                          </svg>
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-slate-800">Window {index + 1}</h4>
                          <p className="text-sm text-slate-600">Window dimensions and properties</p>
                        </div>
                      </div>
                                
                                {/* Dimensions Section */}
                                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200/50 shadow-inner mt-5">
                                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                                        <div className="p-2 bg-blue-200 rounded-lg mr-3 shadow-sm">
                                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                                            </svg>
                                        </div>
                                        Dimensions
                                    </h5>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <label htmlFor={`window-${index}-width`} className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                                                <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                                </svg>
                                                <span>Width (m)</span>
                                            </label>
                                            <input 
                                                type="number" 
                                                inputMode="decimal"
                                                id={`window-${index}-width`}
                                                value={window.width} 
                                                onChange={(e) => handleWindowDetailChange(index, 'width', e.target.value)} 
                                                placeholder="1.40" 
                                                className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-blue-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-300 hover:border-blue-300 hover:bg-white shadow-md backdrop-blur-sm" 
                                                required step="0.01" min="0.1" max="10"
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label htmlFor={`window-${index}-height`} className="flex items-center space-x-1.5 text-xs font-semibold text-slate-700">
                                                <svg className="w-3 h-3 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7l4-4m0 0l4 4m-4-4v18" />
                                                </svg>
                                                <span>Height (m)</span>
                                            </label>
                                            <input 
                                                type="number" 
                                                inputMode="decimal"
                                                id={`window-${index}-height`}
                                                value={window.height} 
                                                onChange={(e) => handleWindowDetailChange(index, 'height', e.target.value)} 
                                                placeholder="1.10" 
                                                className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-blue-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all duration-300 hover:border-blue-300 hover:bg-white shadow-md backdrop-blur-sm" 
                                                required step="0.01" min="0.1" max="10"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Glazing Information Section */}
                                <div className="bg-green-50 rounded-xl p-5 border border-green-200/50 shadow-inner mt-4">
                                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                                        <div className="p-2 bg-green-200 rounded-lg mr-3 shadow-sm">
                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        Glazing Information
                                    </h5>
                                    <div className="space-y-3">
                                        <div className="space-y-1.5">
                                            <label htmlFor={`window-${index}-glazingType`} className="text-xs font-semibold text-slate-700">{requiredMarker}Glazing Type</label>
                                            <select 
                                                id={`window-${index}-glazingType`}
                                                value={window.glazingType} 
                                                onChange={(e) => handleWindowDetailChange(index, 'glazingType', e.target.value)} 
                                                className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-green-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all duration-300 hover:border-green-300 hover:bg-white shadow-md backdrop-blur-sm" 
                                                required
                                            >
                                                {glazingTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label htmlFor={`window-${index}-glazingGap`} className="text-xs font-semibold text-slate-700">{requiredMarker}Glazing Gap</label>
                                            <select 
                                                id={`window-${index}-glazingGap`}
                                                value={window.glazingGap} 
                                                onChange={(e) => handleWindowDetailChange(index, 'glazingGap', e.target.value)} 
                                                className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-green-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-400 transition-all duration-300 hover:border-green-300 hover:bg-white shadow-md backdrop-blur-sm" 
                                                required
                                            >
                                                <option value="">Select glazing gap</option>
                                                <option value="6 mm">6 mm</option>
                                                <option value="12 mm">12 mm</option>
                                                <option value="16 mm or more">16 mm or more</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Properties Section */}
                                <div className="bg-purple-50 rounded-xl p-5 border border-purple-200/50 shadow-inner mt-4">
                                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                                        <div className="p-2 bg-purple-200 rounded-lg mr-3 shadow-sm">
                                            <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                        Properties
                                    </h5>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <label htmlFor={`window-${index}-orientation`} className="text-xs font-semibold text-slate-700">{requiredMarker}Orientation</label>
                                            <select 
                                                id={`window-${index}-orientation`}
                                                value={window.orientation} 
                                                onChange={(e) => handleWindowDetailChange(index, 'orientation', e.target.value)} 
                                                className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-purple-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all duration-300 hover:border-purple-300 hover:bg-white shadow-md backdrop-blur-sm" 
                                                required
                                            >
                                                {orientationOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label htmlFor={`window-${index}-frameType`} className="text-xs font-semibold text-slate-700">{requiredMarker}Frame Type</label>
                                            <select 
                                                id={`window-${index}-frameType`}
                                                value={window.windowType} 
                                                onChange={(e) => handleWindowDetailChange(index, 'windowType', e.target.value)} 
                                                className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-purple-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all duration-300 hover:border-purple-300 hover:bg-white shadow-md backdrop-blur-sm" 
                                                required
                                            >
                                                {frameTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <label htmlFor={`window-${index}-condition`} className="text-xs font-semibold text-slate-700">{requiredMarker}Condition</label>
                                            <select 
                                                id={`window-${index}-condition`}
                                                value={window.condition} 
                                                onChange={(e) => handleWindowDetailChange(index, 'condition', e.target.value)} 
                                                className="w-full px-4 py-3 text-sm bg-white/90 border-2 border-purple-200/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-400 transition-all duration-300 hover:border-purple-300 hover:bg-white shadow-md backdrop-blur-sm" 
                                                required
                                            >
                                                <option value="">Select condition</option>
                                                <option value="good">Good</option>
                                                <option value="average">Average</option>
                                                <option value="poor">Poor</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Features Section */}
                                <div className="bg-amber-50 rounded-xl p-5 border border-amber-200/50 shadow-inner mt-4">
                                    <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                                        <div className="p-2 bg-amber-200 rounded-lg mr-3 shadow-sm">
                                            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2m-8 8v2a2 2 0 002 2h2" />
                                            </svg>
                                        </div>
                                        Features
                                    </h5>
                                    <div className="space-y-4">
                                        {/* Trickle Vents */}
                                        <div>
                                            <label className="text-xs font-semibold text-slate-700 mb-2 block">{requiredMarker}Trickle Vents</label>
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleWindowTrickleVentsChange(index, Math.max(0, window.trickleVents - 1))}
                                                    disabled={window.trickleVents <= 0}
                                                    className="w-12 h-12 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-white hover:bg-slate-50 hover:border-slate-400 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-slate-300 transition-all duration-200 shadow-sm"
                                                >
                                                    <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                                                    </svg>
                                                </button>
                                                <div className="flex-1 flex items-center justify-center h-12 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg shadow-sm">
                                                    <span className="text-2xl font-bold text-blue-900">{window.trickleVents}</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => handleWindowTrickleVentsChange(index, Math.min(20, window.trickleVents + 1))}
                                                    disabled={window.trickleVents >= 20}
                                                    className="w-12 h-12 flex items-center justify-center rounded-lg border-2 border-blue-400 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:border-slate-300 transition-all duration-200 shadow-lg shadow-blue-500/25"
                                                >
                                                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Draught Proofed */}
                                        <div>
                                            <label className="text-xs font-semibold text-slate-700 mb-2 block">{requiredMarker}Draught Proofed</label>
                                            <div className="flex space-x-3">
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleWindowBooleanChange(index, 'isDraughtProofed', true)} 
                                                    className={`flex-1 h-12 px-4 text-sm font-semibold rounded-lg transition-all duration-300 shadow-sm ${
                                                        window.isDraughtProofed === true 
                                                            ? 'bg-green-600 text-white border-2 border-green-400 shadow-lg shadow-green-500/25' 
                                                            : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                                                    }`}
                                                >
                                                    Yes
                                                </button>
                                                <button 
                                                    type="button" 
                                                    onClick={() => handleWindowBooleanChange(index, 'isDraughtProofed', false)} 
                                                    className={`flex-1 h-12 px-4 text-sm font-semibold rounded-lg transition-all duration-300 shadow-sm ${
                                                        window.isDraughtProofed === false 
                                                            ? 'bg-red-600 text-white border-2 border-red-400 shadow-lg shadow-red-500/25' 
                                                            : 'bg-white/80 text-slate-600 border-2 border-slate-200 hover:bg-white hover:border-slate-300 hover:shadow-md backdrop-blur-sm'
                                                    }`}
                                                >
                                                    No
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        ))}
                        
                        {/* General Window Photos */}
                        <div className="bg-purple-50 rounded-xl p-5 border border-purple-200/50 shadow-inner">
                          <h5 className="text-sm font-bold text-slate-800 mb-4 flex items-center">
                            <div className="p-2 bg-purple-200 rounded-lg mr-3 shadow-sm">
                              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                            {requiredMarker}Multiple Window Images
                          </h5>
                          <p className="text-xs text-slate-600 mb-4">All windows in the room</p>
                          <PhotoCapture 
                            onPhotosCaptured={(p) => handlePhotosCaptured('multipleWindowImages', p)} 
                            maxPhotos={999} 
                            buttonLabel="Capture General Window Images" 
                            buttonClass="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-lg transition-all duration-300" 
                          />
                          {renderPhotoPreview(photos.multipleWindowImages, 'multipleWindowImages', 'Window')}
                        </div>
                    </>
                )}
            </div>
          </div>
          
          {/* Form Footer */}
          <div className="mt-8">
            <div className="bg-gradient-to-r from-slate-100 via-slate-50 to-white rounded-2xl px-6 py-6 border border-slate-200/50 shadow-lg">
              <div className="flex flex-col sm:flex-row gap-4 sm:justify-end">
                <button 
                  type="button" 
                  onClick={onCancel} 
                  className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-slate-500 to-slate-600 text-white font-semibold rounded-xl shadow-lg hover:from-slate-600 hover:to-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-500/30 focus:ring-offset-2 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl border-2 border-slate-400/20"
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel
                  </div>
                </button>
                <button 
                  type="submit" 
                  className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold rounded-xl shadow-lg hover:from-emerald-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:ring-offset-2 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl border-2 border-emerald-400/20"
                >
                  <div className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Save Room
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Photo Gallery Modal */}
      {selectedGallery && (
        <PhotoGalleryModal
          isOpen={!!selectedGallery}
          onClose={handleCloseGallery}
          photos={(() => {
            // Handle radiator photos
            if (selectedGallery.startsWith('radiator_') && selectedRadiatorIndex !== null) {
              const radiatorPhotos = photos.radiatorImages.find(
                r => r.radiatorId === `radiator-${selectedRadiatorIndex + 1}`
              )?.images || [];
              return radiatorPhotos.map((photo: DisplayablePhotoData) => ({
                id: photo.displayId,
                url: photo.url || photo.dataUrl,
                localPath: photo.dataUrl,
                fileName: photo.fileName,
              }));
            }
            // Handle regular photo fields (exclude radiatorImages)
            if (selectedGallery === 'wallImages') {
              return photos.wallImages.map((photo: DisplayablePhotoData) => ({
                id: photo.displayId,
                url: photo.url || photo.dataUrl,
                localPath: photo.dataUrl,
                fileName: photo.fileName,
              }));
            } else if (selectedGallery === 'fullHeightImage') {
              return photos.fullHeightImage.map((photo: DisplayablePhotoData) => ({
                id: photo.displayId,
                url: photo.url || photo.dataUrl,
                localPath: photo.dataUrl,
                fileName: photo.fileName,
              }));
            } else if (selectedGallery === 'doorUnderCutsImage') {
              return photos.doorUnderCutsImage.map((photo: DisplayablePhotoData) => ({
                id: photo.displayId,
                url: photo.url || photo.dataUrl,
                localPath: photo.dataUrl,
                fileName: photo.fileName,
              }));
            } else if (selectedGallery === 'ventilationFansImage') {
              return photos.ventilationFansImage.map((photo: DisplayablePhotoData) => ({
                id: photo.displayId,
                url: photo.url || photo.dataUrl,
                localPath: photo.dataUrl,
                fileName: photo.fileName,
              }));
            } else if (selectedGallery === 'lightingImages') {
              return photos.lightingImages.map((photo: DisplayablePhotoData) => ({
                id: photo.displayId,
                url: photo.url || photo.dataUrl,
                localPath: photo.dataUrl,
                fileName: photo.fileName,
              }));
            } else if (selectedGallery === 'multipleWindowImages') {
              return photos.multipleWindowImages.map((photo: DisplayablePhotoData) => ({
                id: photo.displayId,
                url: photo.url || photo.dataUrl,
                localPath: photo.dataUrl,
                fileName: photo.fileName,
              }));
            } else if (selectedGallery === 'secondaryHeatingImages') {
              return photos.secondaryHeatingImages.map((photo: DisplayablePhotoData) => ({
                id: photo.displayId,
                url: photo.url || photo.dataUrl,
                localPath: photo.dataUrl,
                fileName: photo.fileName,
              }));
            }
            return [];
          })()}
          sectionTitle={selectedGallery.startsWith('radiator_') 
            ? `Radiator ${selectedRadiatorIndex !== null ? selectedRadiatorIndex + 1 : ''} Images` 
            : selectedGallery.replace(/([A-Z])/g, ' $1').trim()}
          onDeletePhoto={(photoId) => {
            if (selectedGallery.startsWith('radiator_') && selectedRadiatorIndex !== null) {
              removePhoto(selectedRadiatorIndex, photoId);
            } else {
              removePhoto(selectedGallery as keyof RoomPhotos, photoId);
            }
          }}
          onUpdateNote={() => {}}
          onGalleryStateChange={onGalleryStateChange}
        />
      )}
    </div>
  );
};

export default RoomSurveyContainer; 